import { useState, useEffect, useCallback, useRef } from 'react';
import { IS_CAPACITOR } from '../api/config';
import { getAppleMusicStatus } from '../api/authClient';
import {
  getMusicKitInstance,
  isAppleMusicAuthorized,
  authorizeAppleMusic,
  unauthorizeAppleMusic,
  onAuthorizationChange,
} from '../api/appleMusicClient';

// Whether the server has MusicKit credentials, cached at module level so the
// settings modal and export drawer don't each probe it. A definitive answer
// (true or false) is kept; transient failures clear the cache so a later
// mount retries.
let configuredPromise = null;

function fetchConfigured() {
  if (!configuredPromise) {
    configuredPromise = getAppleMusicStatus()
      .then(async (res) => {
        if (!res.ok) throw new Error('status probe failed');
        const data = await res.json();
        return !!data.configured;
      })
      .catch(() => {
        configuredPromise = null;
        return false;
      });
  }
  return configuredPromise;
}

// Single gate for Apple Music connect state. Inert unless the user's music
// service is apple-music, so Spotify users never load MusicKit or probe the
// server.
export function useAppleMusicAuth(musicService) {
  const active = musicService === 'apple-music';
  const [configured, setConfigured] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let unsubscribe = null;

    (async () => {
      const ok = await fetchConfigured();
      if (cancelled) return;
      setConfigured(ok);
      if (!ok) return;
      // Pre-warm MusicKit on web so a later Connect click can open the popup
      // inside the user gesture. Failure is fine — the account-stored token
      // can still report connected, and connect() retries the load.
      if (!IS_CAPACITOR) {
        try { await getMusicKitInstance(); } catch {}
      }
      try {
        const authed = await isAppleMusicAuthorized();
        if (!cancelled) setIsAuthorized(authed);
        if (!IS_CAPACITOR && !cancelled) {
          unsubscribe = await onAuthorizationChange((next) => {
            // Only trust "authorized" flips from MusicKit events; an
            // unauthorized flip may just mean this device has no local
            // token while the account token still connects it.
            if (!cancelled && mountedRef.current && next) setIsAuthorized(true);
          });
        }
      } catch {
        // Status probe failed — leave unauthorized; connect() will retry.
      }
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [active]);

  const connect = useCallback(async () => {
    setConnecting(true);
    setError('');
    try {
      await authorizeAppleMusic();
      if (mountedRef.current) setIsAuthorized(true);
      return true;
    } catch (err) {
      if (mountedRef.current) {
        setError(err?.message || 'Could not connect to Apple Music.');
      }
      return false;
    } finally {
      if (mountedRef.current) setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setError('');
    try {
      await unauthorizeAppleMusic();
    } catch {
      // Dropping local state is what matters; ignore SDK teardown errors.
    }
    if (mountedRef.current) setIsAuthorized(false);
  }, []);

  return { configured, isAuthorized, connecting, connect, disconnect, error };
}
